import { FIELD_TYPE_PRODUCT_IMAGE } from '@frontend/constants/type-fields-of-template';
import TemplateField from '@/banner_template/fields/TemplateField';

export const addProductImage = async ({ scope, state, instanceId, copyObject, name, transferTargetTopRight = null }) => {
  const { selectedInstance } = state;
  const { template, data, canvas } = selectedInstance;

  // Validate that we have a file to copy
  const newFileId = copyObject.fileName;
  if (!newFileId) {
    console.warn('Product Image copy: No fileName found in copyObject');
    return;
  }

  // For Product Images, use the original x_offset/y_offset from the source preview data
  // These are relative offsets that work with the Product Dimension positioning system
  const offsetX = copyObject.productImageXOffset ?? 0;
  const offsetY = copyObject.productImageYOffset ?? 0;
  const originalScale = copyObject.productImageScale ?? copyObject.realScaleX ?? copyObject.scaleX ?? 1;

  const productImageFields = template.fields.filter(field => field.type === FIELD_TYPE_PRODUCT_IMAGE);
  const existingFileIds = data.file_ids?.split(',').filter(Boolean) || [];

  const isObjectTransfer = Boolean(transferTargetTopRight || copyObject.isObjectTransfer);
  const sourceTemplateWidth = copyObject.sourceTemplateWidth;
  const sourceTemplateHeight = copyObject.sourceTemplateHeight;
  const targetTemplateWidth = canvas?.width;
  const targetTemplateHeight = canvas?.height;
  let scaleFactor = 1;

  if (!isObjectTransfer && sourceTemplateWidth && sourceTemplateHeight && targetTemplateWidth && targetTemplateHeight) {
    const sourceAspectRatio = sourceTemplateWidth / sourceTemplateHeight;
    const targetAspectRatio = targetTemplateWidth / targetTemplateHeight;

    if (sourceAspectRatio > targetAspectRatio) {
      scaleFactor = targetTemplateWidth / sourceTemplateWidth;
    } else {
      scaleFactor = targetTemplateHeight / sourceTemplateHeight;
    }
  }

  const parsedOffsetX = parseFloat(offsetX) * scaleFactor;
  const parsedOffsetY = parseFloat(offsetY) * scaleFactor;
  const parsedScale = parseFloat(originalScale) * scaleFactor;
  const sourceWidth = copyObject.scaledWidth || (copyObject.width * (copyObject.scaleX || 1));
  const sourceHeight = copyObject.scaledHeight || (copyObject.height * (copyObject.scaleY || 1));
  const targetWidth = sourceWidth * scaleFactor;
  const targetHeight = sourceHeight * scaleFactor;

  let targetProductIndex = existingFileIds.length;
  let targetProductElementId = null;

  try {
    if (productImageFields.length > existingFileIds.length) {
      // Add the product image file and update the offset/scale for the new index
      const newIndex = existingFileIds.length;
      targetProductIndex = newIndex;
      targetProductElementId = productImageFields[newIndex]?.element_id || null;

      scope.addFileIdForProductImage(newFileId, instanceId);

      // Update the offset and scale for the newly added product image
      const preview = selectedInstance;
      if (preview?.data) {
        // Initialize arrays if needed
        if (!preview.data.x_offset) preview.data.x_offset = [];
        if (!preview.data.y_offset) preview.data.y_offset = [];
        if (!preview.data.scale) preview.data.scale = [];
        if (!preview.data.p_width) preview.data.p_width = [];
        if (!preview.data.p_height) preview.data.p_height = [];
        if (!preview.data.p_opacity) preview.data.p_opacity = [];
        if (!preview.data.angle) preview.data.angle = [];
        if (!preview.data.moveable) preview.data.moveable = [];
        if (!preview.data.p_mirror_shadows) preview.data.p_mirror_shadows = [];
        if (!preview.data.p_oval_shadows) preview.data.p_oval_shadows = [];

        // Set the values for the new product image
        preview.data.x_offset[newIndex] = parsedOffsetX;
        preview.data.y_offset[newIndex] = parsedOffsetY;
        preview.data.scale[newIndex] = parsedScale;
        preview.data.p_width[newIndex] = targetWidth;
        preview.data.p_height[newIndex] = targetHeight;
        preview.data.p_opacity[newIndex] = copyObject.opacity ?? 1;
        preview.data.angle[newIndex] = copyObject.angle || 0;
        preview.data.moveable[newIndex] = 'Yes';
        preview.data.p_mirror_shadows[newIndex] = copyObject.mirrorShadow || null;
        preview.data.p_oval_shadows[newIndex] = copyObject.ovalShadow || null;

        // Persist the changes
        scope.updateLayoutChanges(instanceId, 'x_offset', preview.data.x_offset);
        scope.updateLayoutChanges(instanceId, 'y_offset', preview.data.y_offset);
        scope.updateLayoutChanges(instanceId, 'scale', preview.data.scale);
        scope.updateLayoutChanges(instanceId, 'p_width', preview.data.p_width);
        scope.updateLayoutChanges(instanceId, 'p_height', preview.data.p_height);
        scope.updateLayoutChanges(instanceId, 'p_opacity', preview.data.p_opacity);
        scope.updateLayoutChanges(instanceId, 'angle', preview.data.angle);
        scope.updateLayoutChanges(instanceId, 'moveable', preview.data.moveable);
        scope.updateLayoutChanges(instanceId, 'p_mirror_shadows', preview.data.p_mirror_shadows);
        scope.updateLayoutChanges(instanceId, 'p_oval_shadows', preview.data.p_oval_shadows);

        // Apply shadow properties for product images using element_id pattern
        // Product images use element_id from the existing field
        const productImageField = productImageFields[newIndex];
        if (productImageField?.element_id && (copyObject.shadow || copyObject.outlineShadow || copyObject.mirrorShadow || copyObject.ovalShadow)) {
          const elementId = productImageField.element_id;
          if (copyObject.shadow) {
            const shadowValue = typeof copyObject.shadow === 'string' ? copyObject.shadow : JSON.stringify(copyObject.shadow);
            scope.updateLayoutChanges(instanceId, `${elementId}_custom_shadow`, shadowValue);
          }
          if (copyObject.outlineShadow) {
            const outlineShadowValue = typeof copyObject.outlineShadow === 'string' ? copyObject.outlineShadow : JSON.stringify(copyObject.outlineShadow);
            scope.updateLayoutChanges(instanceId, `${elementId}_custom_outline_shadow`, outlineShadowValue);
          }
          if (copyObject.mirrorShadow) {
            const mirrorShadowValue = typeof copyObject.mirrorShadow === 'string' ? copyObject.mirrorShadow : JSON.stringify(copyObject.mirrorShadow);
            scope.updateLayoutChanges(instanceId, `${elementId}_custom_mirror_shadow`, mirrorShadowValue);
          }
          if (copyObject.ovalShadow) {
            const ovalShadowValue = typeof copyObject.ovalShadow === 'string' ? copyObject.ovalShadow : JSON.stringify(copyObject.ovalShadow);
            scope.updateLayoutChanges(instanceId, `${elementId}_custom_oval_shadow`, ovalShadowValue);
          }
        }
      }
    } else {
      const hasProductDimensionField = scope.checkHasTemplateProductDimensionField(window.preview_data, instanceId);

      const newFields = await scope.addProductDimAndGTIN(!hasProductDimensionField, selectedInstance, template.fields);

      if (newFields?.newProductDimensionField) {
        const productDimensionOptions = JSON.parse(newFields.newProductDimensionField.options);
        scope.canvas_data[instanceId].product = {
          left: parseInt(productDimensionOptions.X, 10),
          top: parseInt(productDimensionOptions.Y, 10),
          width: parseInt(productDimensionOptions.Width, 10),
          height: parseInt(productDimensionOptions.Height, 10),
          alignment: productDimensionOptions.Alignment,
        };
      }

      const defaultData = {
        angle: copyObject.angle || 0,
        scale: parsedScale,
        p_flipX: copyObject.flipX || 0,
        p_flipY: copyObject.flipY || 0,
        p_opacity: copyObject.opacity ?? 1,
        p_width: targetWidth,
        p_height: targetHeight,
        x_offset: parsedOffsetX,
        y_offset: parsedOffsetY,
        moveable: 'Yes',
        p_mirror_shadow: copyObject.mirrorShadow || null,
        p_oval_shadow: copyObject.ovalShadow || null,
      };

      const [newProductImageField] = await scope.addProductImageFields(
        selectedInstance,
        canvas,
        [newFileId],
        scope.getStartIndex(instanceId),
        {
          Name: name || undefined,
          Width: targetWidth,
          Height: targetHeight,
          defaultData
        }
      );

      if (newProductImageField) {
        targetProductElementId = newProductImageField.element_id || null;
        const newProductOptions = { ...JSON.parse(newProductImageField.options), id: newProductImageField.id };
        scope.canvas_data[instanceId].product_image_settings.push(newProductOptions);
        scope.canvas_data[instanceId].templateInstance.fields.push(new TemplateField(newProductImageField));

        scope.updateLayoutChanges(instanceId, 'file_ids', [...existingFileIds, newFileId].join(','));

        // Apply shadow properties for product images using element_id pattern
        // Product images use element_id like \"product_image_image_0\", \"product_image_image_1\", etc.
        const elementId = newProductImageField.element_id;
        if (elementId && (copyObject.shadow || copyObject.outlineShadow || copyObject.mirrorShadow || copyObject.ovalShadow)) {
          if (copyObject.shadow) {
            const shadowValue = typeof copyObject.shadow === 'string' ? copyObject.shadow : JSON.stringify(copyObject.shadow);
            scope.updateLayoutChanges(instanceId, `${elementId}_custom_shadow`, shadowValue);
          }
          if (copyObject.outlineShadow) {
            const outlineShadowValue = typeof copyObject.outlineShadow === 'string' ? copyObject.outlineShadow : JSON.stringify(copyObject.outlineShadow);
            scope.updateLayoutChanges(instanceId, `${elementId}_custom_outline_shadow`, outlineShadowValue);
          }
          if (copyObject.mirrorShadow) {
            const mirrorShadowValue = typeof copyObject.mirrorShadow === 'string' ? copyObject.mirrorShadow : JSON.stringify(copyObject.mirrorShadow);
            scope.updateLayoutChanges(instanceId, `${elementId}_custom_mirror_shadow`, mirrorShadowValue);
          }
          if (copyObject.ovalShadow) {
            const ovalShadowValue = typeof copyObject.ovalShadow === 'string' ? copyObject.ovalShadow : JSON.stringify(copyObject.ovalShadow);
            scope.updateLayoutChanges(instanceId, `${elementId}_custom_oval_shadow`, ovalShadowValue);
          }
        }
      } else {
        console.warn('Product Image copy: Failed to create new product image field');
        // Fallback: try to add as additional file anyway
        scope.updateLayoutChanges(instanceId, 'file_ids', [...existingFileIds, newFileId].join(','));
      }
    }

    await scope.reDrawProductImage(
      instanceId,
      true,
      false,
      false,
      {updateLayerOrder: !isObjectTransfer}
    );

    if (transferTargetTopRight) {
      const productImageObjects = canvas.getObjects().filter((object) => (
        object.fieldType === FIELD_TYPE_PRODUCT_IMAGE
      ));
      const expectedObjectId = `image_${targetProductIndex}`;
      const productObject = productImageObjects.find(
        (object) => object.id === expectedObjectId
      );

      if (!productObject || productObject.fileName !== newFileId) {
        throw new Error(
          `Transferred Product Image was resolved incorrectly: expected `
          + `${expectedObjectId} (${newFileId}), got `
          + `${productObject?.id || 'none'} (${productObject?.fileName || 'none'})`
        );
      }

      if (
        targetProductElementId
          && productObject.element_id !== targetProductElementId
      ) {
        console.warn(
          `Transferred Product Image element mismatch: expected `
          + `${targetProductElementId}, got ${productObject.element_id || 'none'}`
        );
      }

      if (sourceWidth && sourceHeight && productObject.width && productObject.height) {
        productObject.set({
          scaleX: sourceWidth / productObject.width,
          scaleY: sourceHeight / productObject.height,
        });
        productObject.setCoords?.();
      }

      productObject.setCoords?.();
      const topRight = productObject.getCoords?.()[1];

      if (topRight) {
        productObject.set({
          left: productObject.left + (transferTargetTopRight.x - topRight.x),
          top: productObject.top + (transferTargetTopRight.y - topRight.y),
        });
        productObject.setCoords?.();

        if (typeof scope.handleProductImageMoving === 'function') {
          scope.handleProductImageMoving(
            productObject.left,
            productObject.top,
            productObject,
            targetProductIndex,
            instanceId,
            canvas,
            false, // newAngle
            false, // resizing
            false, // isBulkProductImagesUpdate
            false // updateFileId
          );
        }

        const preview = selectedInstance;
        const originScale = scope.canvas_data?.[instanceId]?.originCoords?.[targetProductIndex]?.scaleX || 1;
        const exactScale = productObject.scaleX / originScale;
        const productWidth = productObject.width * productObject.scaleX;
        const productHeight = productObject.height * productObject.scaleY;

        if (preview?.data) {
          if (!preview.data.scale) preview.data.scale = [];
          if (!preview.data.p_width) preview.data.p_width = [];
          if (!preview.data.p_height) preview.data.p_height = [];

          preview.data.scale[targetProductIndex] = exactScale;
          preview.data.p_width[targetProductIndex] = productWidth;
          preview.data.p_height[targetProductIndex] = productHeight;
        }

        scope.updateLayoutChanges(instanceId, 'scale', preview.data.scale);
        scope.updateLayoutChanges(instanceId, 'p_width', preview.data.p_width);
        scope.updateLayoutChanges(instanceId, 'p_height', preview.data.p_height);
      }
    }
  } catch (error) {
    console.error('Product Image copy: Error adding product image', error);

    if (isObjectTransfer) {
      throw error;
    }

    // Attempt fallback: try to add file id anyway
    try {
      scope.updateLayoutChanges(instanceId, 'file_ids', [...existingFileIds, newFileId].join(','));
      scope.reDrawProductImage(instanceId);
    } catch (fallbackError) {
      console.error('Product Image copy: Fallback also failed', fallbackError);
    }
  }
};
